import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Dialog} from 'primereact/dialog'
import {forwardRef, useCallback, useImperativeHandle} from 'react'
import {ReactComponent as AiOutlineLineChart} from '../../../assets/icons/AiOutlineLineChart.svg'
import {ReactComponent as IoChatBubbles} from '../../../assets/icons/IoChatBubbles.svg'
import {ReactComponent as TbDeviceWatch} from '../../../assets/icons/TbDeviceWatch.svg'
import {ReactComponent as VscReport} from '../../../assets/icons/VscReport.svg'

import {useMutation} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {InputSwitch} from 'primereact/inputswitch'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../../utils/BlockScrollsUtils'
import {useAuth} from '../../authentication/AuthProvider'
import {updateAccessConfig} from '../../patient/PatientEndPoint'
import {usePatientContext} from './StyleDetailInformationWrapper'

const patientPortalSettingKeys = {
    watchInteraction: 'watchInteraction',
    allowToAccessPatientPage: 'allowToAccessPatientPage',
    allowToReportSymptom: 'allowToReportSymptom',
    allowToAddObservation: 'allowToAddObservation',
    allowToChatWithDoctors: 'allowToChatWithDoctors',
    allowToViewActivities: 'allowToViewActivities',
    allowToViewNewestObservations: 'allowToViewNewestObservations',
}

const StyledPatientPortalSettingsForm = forwardRef(({users = []}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    const {currentViewedUser, handleChangeCurrentViewedUser} =
        usePatientContext()
    //#endregion

    //#region Translate
    const {translateKeys, translateCommons} = TranslateUtils()
    //#endregion

    //#region State
    const settingList = [
        {
            key: patientPortalSettingKeys.allowToAccessPatientPage,
            label: 'Patient Page Access',
            icon: (
                <i className={`pi pi-sign-in `} style={{fontSize: '20px'}}></i>
            ),
            description: 'Allow this patient access to the patient dashboard.',
        },
        {
            key: patientPortalSettingKeys.watchInteraction,
            label: 'Watch Interaction',
            icon: <TbDeviceWatch height={'26px'} width={'26px'} />,
            description:
                'Enable interaction with the smartwatch to send health data to the system (This requires that the patient is assigned to the watch).',
        },

        {
            key: patientPortalSettingKeys.allowToAddObservation,
            label: 'Add Observations',
            icon: (
                <i
                    className={`pi pi-calendar-plus `}
                    style={{fontSize: '20px'}}></i>
            ),
            description:
                'Authorized to add observations for doctors to monitor this patient’s health.',
        },
        {
            key: patientPortalSettingKeys.allowToChatWithDoctors,
            label: 'Chat with Doctors',
            icon: <IoChatBubbles height={'24px'} width={'24px'} />,
            description:
                'Chat with the doctor for consultation about the patient’s health and current condition.',
        },
        {
            key: patientPortalSettingKeys.allowToReportSymptom,
            label: 'Report Symptom',
            icon: <VscReport height={'20px'} width={'20px'} />,
            description:
                'Authorized to report symptoms to the doctors assigned to care for and monitor this patient’s health.',
        },
        {
            key: patientPortalSettingKeys.allowToViewActivities,
            label: 'View Recent Activities',
            description:
                'View this patient’s health data chart on the patient dashboard.',
            icon: <AiOutlineLineChart height={'24px'} width={'24px'} />,
        },
        {
            key: patientPortalSettingKeys.allowToViewNewestObservations,
            label: 'View Newest Observations',
            icon: (
                <i className={`pi pi-chart-bar`} style={{fontSize: '20px'}}></i>
            ),
            description:
                'View the latest health metrics for this patient on the patient dashboard.',
            command: () => {},
        },
    ]

    const visible = useSignal(false)
    //#endregion

    //#region Handle Function
    const handleShowForm = () => {
        blockOverflownScroll()
        visible.value = true
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        visible.value = false
    }

    const {mutate: handleUpdateAccessConfig} = useMutation(
        toPostData =>
            updateAccessConfig(user?.id, currentViewedUser?.id, toPostData),
        {
            onSuccess: data => {
                if (data && data?.id) {
                    if (handleChangeCurrentViewedUser) {
                        handleChangeCurrentViewedUser(data)
                    }
                }
            },
        },
    )

    const handleOnUpdateAccessConfig = useCallback(
        (type, value) => {
            let toPostData
            if (currentViewedUser && currentViewedUser?.accessConfig) {
                toPostData = {
                    ...currentViewedUser?.accessConfig,
                    [type]: value,
                }
            } else {
                toPostData = {
                    [type]: value,
                }
            }

            handleUpdateAccessConfig(toPostData)
        },
        [currentViewedUser, handleUpdateAccessConfig],
    )
    //#endregion

    //#endregion

    return (
        <Dialog
            header={
                <h5 className="mb-0 font-bold">
                    <strong>
                        {translateCommons('Patient Portal Settings')}
                    </strong>
                </h5>
            }
            draggable={false}
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll={true}
            style={{width: '50vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}>
            <div className="w-full">
                <div className="mb-2">
                    <h4 className="font-bold mb-1">
                        {translateKeys('Change Setting')}
                    </h4>
                    <hr className="highlighter" />
                </div>
                <p>
                    Authorized to modify this patient's access permissions and
                    display settings on the website.
                </p>

                <div className="col-12">
                    {settingList?.map(setting => {
                        return (
                            <div
                                key={setting.key}
                                className="flex align-items-center justify-content-between mb-3">
                                <div className="col-10">
                                    <div className="flex align-items-center">
                                        <div
                                            className="mr-3"
                                            style={{
                                                height: '24px',
                                                width: '24px',
                                            }}>
                                            {setting.icon}
                                        </div>
                                        <div className="flex flex-column">
                                            <h6 className="mb-0 font-semibold">
                                                {setting?.label}
                                            </h6>
                                            <span>{setting?.description}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-2">
                                    <div className="flex justify-content-end">
                                        <InputSwitch
                                            checked={
                                                !!currentViewedUser?.accessConfig &&
                                                !!currentViewedUser
                                                    ?.accessConfig[setting?.key]
                                            }
                                            onChange={e =>
                                                handleOnUpdateAccessConfig(
                                                    setting?.key,
                                                    e.value,
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div className="mt-3 col-12">
                    <Button
                        className="col-12 p-button-lg p-button-outlined"
                        label={translateCommons('CLOSE')}
                        icon="pi pi-times"
                        onClick={handleHideForm}
                    />
                </div>
            </div>
        </Dialog>
    )
})

export default StyledPatientPortalSettingsForm
